/**
 * Commission rates — what Medstown keeps, and therefore what every pharmacy is
 * paid.
 *
 * Ops thinks in "80% of MRP", so that is what the table leads with; the
 * commission percentage is the same number from the other side and both are
 * shown. Saving is PIN-gated and never overwrites the previous rates: the
 * server publishes a new version and every settlement records the version it
 * was priced under.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  Alert,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { MdHistory, MdInfoOutline, MdRestartAlt } from "react-icons/md";
import API from "Config/API";
import { toast } from "react-toastify";
import PharmacyCommissionDialog from "./PharmacyCommissionDialog";

import { T } from "theme/tokens";

const dateLabel = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
};

export default function CommissionRates() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState([]);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  // Pharmacies that deviate from the standard card. The rate card is only half
  // the picture — "who is NOT on it" is the other half.
  const [custom, setCustom] = useState([]);
  const [editing, setEditing] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, customRes] = await Promise.all([
        API.get(`/admin/commission`),
        API
          .get(`/admin/commission/pharmacies`)
          .then((r) => r.data?.list || [])
          .catch(() => []),
      ]);
      setCustom(customRes);
      setConfig(data?.config || null);
      setHistory(data?.history || []);
      setDraft(
        (data?.rules || []).map((r) => ({
          key: r.key,
          label: r.label,
          commissionPercent: String(r.commissionPercent),
          isDefault: r.isDefault,
        }))
      );
      setError("");
    } catch (err) {
      setError(
        err?.response?.data?.message || "Could not load the commission rates."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const original = (key) =>
    config?.rules?.find((r) => r.key === key)?.commissionPercent;

  const changed = draft.some(
    (r) => Number(r.commissionPercent) !== original(r.key) ||
      r.label !== config?.rules?.find((x) => x.key === r.key)?.label
  );

  const setRule = (key, field, value) =>
    setDraft((rows) =>
      rows.map((r) => (r.key === key ? { ...r, [field]: value } : r))
    );

  const validate = () => {
    for (const r of draft) {
      const pct = Number(r.commissionPercent);
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
        return `Commission for "${r.label}" must be between 0 and 100.`;
      }
      if (!r.label.trim()) return "Every product type needs a name.";
    }
    if (note.trim().length < 3) {
      return "Say why the rates are changing — it goes on the record.";
    }
    return null;
  };

  const openConfirm = () => {
    const problem = validate();
    if (problem) return setError(problem);
    setError("");
    setPin("");
    setConfirmOpen(true);
  };

  const save = async () => {
    if (!/^\d{4,8}$/.test(pin)) return setError("Enter the 4–8 digit PIN.");
    setSaving(true);
    setError("");
    try {
      const { data } = await API.post(`/admin/commission`, {
        pin,
        note: note.trim(),
        defaultRuleKey:
          draft.find((r) => r.isDefault)?.key || config?.defaultRuleKey,
        rules: draft.map((r) => {
          const existing = config?.rules?.find((x) => x.key === r.key) || {};
          return {
            ...existing,
            key: r.key,
            label: r.label.trim(),
            commissionPercent: Number(r.commissionPercent),
          };
        }),
      });
      toast.success(data?.message || "Commission rates saved.");
      setConfirmOpen(false);
      setNote("");
      await load();
    } catch (err) {
      setError(
        err?.response?.data?.message || "Could not save the commission rates."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card elevation={0} sx={{ border: `1px solid ${T.border}`, borderRadius: 2, p: 2 }}>
        <Skeleton height={30} width={220} />
        <Skeleton height={120} sx={{ mt: 1 }} />
      </Card>
    );
  }

  return (
    <Stack spacing={2}>
      <Card
        elevation={0}
        sx={{ border: `1px solid ${T.border}`, borderRadius: 2, overflow: "hidden" }}
      >
        <Box sx={{ p: 2, borderBottom: `1px solid ${T.borderSoft}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box>
              <Typography sx={{ fontWeight: 800, color: T.primary, fontSize: "1.05rem" }}>
                Commission &amp; settlement
              </Typography>
              <Typography sx={{ fontSize: "0.82rem", color: T.muted, mt: 0.25 }}>
                A pharmacy is settled a percentage of MRP. Changing these changes
                what every partner earns on orders delivered from then on.
              </Typography>
            </Box>
            {config && (
              <Chip
                size="small"
                label={`Version ${config.version}`}
                sx={{
                  fontFamily: "monospace",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  color: T.primary,
                  bgcolor: `${T.primary}12`,
                  border: `1px solid ${T.primary}33`,
                }}
              />
            )}
          </Stack>
        </Box>

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    bgcolor: "#f8fafc",
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    color: T.muted,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    borderBottom: `1px solid ${T.border}`,
                  },
                }}
              >
                <TableCell>Product type</TableCell>
                <TableCell align="center" width={160}>Commission</TableCell>
                <TableCell align="center" width={180}>Pharmacy settlement</TableCell>
                <TableCell align="right" width={170}>On a ₹500 MRP item</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {draft.map((rule) => {
                const pct = Number(rule.commissionPercent);
                const valid = Number.isFinite(pct) && pct >= 0 && pct <= 100;
                const settlement = valid ? 100 - pct : null;
                const onFiveHundred = valid ? (50000 * (100 - pct)) / 100 : null;
                const wasChanged = valid && pct !== original(rule.key);

                return (
                  <TableRow key={rule.key} sx={{ "& td": { borderBottom: `1px solid ${T.borderSoft}` } }}>
                    <TableCell>
                      <TextField
                        size="small"
                        variant="standard"
                        value={rule.label}
                        onChange={(e) => setRule(rule.key, "label", e.target.value)}
                        InputProps={{ sx: { fontSize: "0.85rem", fontWeight: 600 } }}
                      />
                      <Typography
                        sx={{ fontSize: "0.65rem", color: T.subtle, fontFamily: "monospace", mt: 0.25 }}
                      >
                        {rule.key}
                        {rule.isDefault ? " · default" : ""}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <TextField
                        size="small"
                        value={rule.commissionPercent}
                        onChange={(e) =>
                          setRule(
                            rule.key,
                            "commissionPercent",
                            e.target.value.replace(/[^0-9.]/g, "")
                          )
                        }
                        error={!valid}
                        inputProps={{
                          inputMode: "decimal",
                          style: { textAlign: "center", fontWeight: 700 },
                        }}
                        InputProps={{ endAdornment: <span style={{ color: T.muted }}>%</span> }}
                        sx={{ width: 110 }}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Typography sx={{ fontWeight: 800, color: T.success, fontSize: "0.95rem" }}>
                        {settlement === null ? "—" : `${settlement}% of MRP`}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: T.ink }}>
                        {onFiveHundred === null
                          ? "—"
                          : `₹${(onFiveHundred / 100).toFixed(2)}`}
                      </Typography>
                      <Typography sx={{ fontSize: "0.65rem", color: T.subtle }}>
                        to the pharmacy
                        {wasChanged && (
                          <span style={{ color: T.warning, fontWeight: 700 }}>
                            {" "}· changed
                          </span>
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ p: 2, borderTop: `1px solid ${T.borderSoft}` }}>
          <Stack spacing={1.5}>
            <TextField
              size="small"
              fullWidth
              label="Why are these changing?"
              placeholder="e.g. Revised partner agreement effective this month"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />

            {error && <Alert severity="error">{error}</Alert>}

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1}>
              <Button
                variant="contained"
                disabled={!changed}
                onClick={openConfirm}
                sx={{ bgcolor: T.primary, "&:hover": { bgcolor: "#013737" } }}
              >
                Save rates
              </Button>
              <Button
                startIcon={<MdRestartAlt />}
                disabled={!changed}
                onClick={() => {
                  load();
                  setNote("");
                  setError("");
                }}
                sx={{ color: T.muted }}
              >
                Discard changes
              </Button>
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Orders already settled keep the rates they were priced under.">
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: T.subtle }}>
                  <MdInfoOutline size={15} />
                  <Typography sx={{ fontSize: "0.72rem" }}>
                    Applies to orders delivered from now on
                  </Typography>
                </Stack>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>
      </Card>

      <Card elevation={0} sx={{ border: `1px solid ${T.border}`, borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography sx={{ fontWeight: 800, color: T.primary, fontSize: "0.95rem" }}>
            Pharmacies on negotiated rates
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: T.muted, mt: 0.25 }}>
            {custom.length === 0
              ? "Every pharmacy is on the standard rates above."
              : `${custom.length} pharmac${custom.length === 1 ? "y is" : "ies are"} settled at their own rates and are not affected by changes above. Set one from the Wallets tab.`}
          </Typography>
        </Box>

        {custom.length > 0 && (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      bgcolor: "#f8fafc",
                      fontWeight: 700,
                      fontSize: "0.66rem",
                      color: T.muted,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      borderBottom: `1px solid ${T.border}`,
                    },
                  }}
                >
                  <TableCell>Pharmacy</TableCell>
                  <TableCell>Their rates</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell align="right" width={110} />
                </TableRow>
              </TableHead>
              <TableBody>
                {custom.map((c) => (
                  <TableRow
                    key={c.pharmacyId}
                    hover
                    sx={{ "& td": { borderBottom: `1px solid ${T.borderSoft}` } }}
                  >
                    <TableCell>
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: T.ink }}>
                        {c.pharmacyName || "Unnamed pharmacy"}
                      </Typography>
                      <Typography sx={{ fontSize: "0.66rem", color: T.subtle, fontFamily: "monospace" }}>
                        {c.medstownId || c.pharmacyId}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" flexWrap="wrap" gap={0.75}>
                        {c.rates.map((r) => {
                          const better = r.commissionPercent < r.globalCommissionPercent;
                          return (
                            <Chip
                              key={r.key}
                              size="small"
                              label={`${r.label} ${r.commissionPercent}%`}
                              sx={{
                                height: 20,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                color: better ? T.success : T.warning,
                                bgcolor: better ? `${T.success}15` : `${T.warning}15`,
                              }}
                            />
                          );
                        })}
                      </Stack>
                      <Typography sx={{ fontSize: "0.64rem", color: T.subtle, mt: 0.4 }}>
                        standard: {c.rates
                          .map((r) => `${r.label} ${r.globalCommissionPercent}%`)
                          .join(" · ")}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography sx={{ fontSize: "0.74rem", color: T.muted }}>
                        {c.note || "—"}
                      </Typography>
                      <Typography sx={{ fontSize: "0.64rem", color: T.subtle }}>
                        v{c.version} · {dateLabel(c.updatedAt)} · {c.updatedBy}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() =>
                          setEditing({
                            pharmacyId: c.pharmacyId,
                            pharmacyName: c.pharmacyName,
                          })
                        }
                        sx={{ fontSize: "0.7rem", color: T.primary }}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {history.length > 1 && (
        <Card elevation={0} sx={{ border: `1px solid ${T.border}`, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.75, pb: 1 }}>
            <MdHistory size={16} color={T.muted} />
            <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: T.ink }}>
              Rate history
            </Typography>
          </Stack>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 620 }}>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.version} sx={{ "& td": { borderBottom: `1px solid ${T.borderSoft}` } }}>
                    <TableCell width={70}>
                      <Typography sx={{ fontFamily: "monospace", fontSize: "0.72rem", color: T.primary, fontWeight: 700 }}>
                        v{h.version}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "0.78rem", color: T.ink }}>
                        {(h.rules || [])
                          .map((r) => `${r.label} ${r.commissionPercent}%`)
                          .join("  ·  ")}
                      </Typography>
                      {h.note && (
                        <Typography sx={{ fontSize: "0.7rem", color: T.muted, mt: 0.25 }}>
                          {h.note}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" width={200}>
                      <Typography sx={{ fontSize: "0.7rem", color: T.muted }}>
                        {dateLabel(h.createdAt)}
                      </Typography>
                      <Typography sx={{ fontSize: "0.66rem", color: T.subtle }}>
                        {h.createdBy}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <PharmacyCommissionDialog
        open={!!editing}
        pharmacy={editing}
        onClose={() => setEditing(null)}
        onDone={load}
      />

      {/* PIN confirmation */}
      <Dialog open={confirmOpen} onClose={saving ? undefined : () => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: T.ink }}>Save commission rates</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Alert severity="warning" sx={{ fontSize: 13 }}>
              This changes what every pharmacy earns on orders delivered from now
              on. Orders already settled keep the rates they were priced under.
            </Alert>

            <Box sx={{ border: `1px solid ${T.border}`, borderRadius: 2, p: 1.5 }}>
              {draft.map((r) => {
                const before = original(r.key);
                const after = Number(r.commissionPercent);
                return (
                  <Stack
                    key={r.key}
                    direction="row"
                    justifyContent="space-between"
                    sx={{ py: 0.4 }}
                  >
                    <Typography sx={{ fontSize: "0.82rem" }}>{r.label}</Typography>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>
                      {before !== after ? (
                        <>
                          <span style={{ color: T.muted, textDecoration: "line-through" }}>
                            {before}%
                          </span>{" "}
                          <span style={{ color: T.warning }}>{after}%</span>
                        </>
                      ) : (
                        <span style={{ color: T.muted }}>{after}%</span>
                      )}
                    </Typography>
                  </Stack>
                );
              })}
            </Box>

            <TextField
              label="Admin PIN"
              type="password"
              size="small"
              fullWidth
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              autoComplete="off"
              inputProps={{ inputMode: "numeric" }}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving} sx={{ color: T.muted }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving}
            sx={{ bgcolor: T.primary, "&:hover": { bgcolor: "#013737" } }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : "Save rates"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
